import { Dropdown, Tag, Typography } from 'antd';
import type { MenuProps } from 'antd';
import { MoreOutlined, PushpinFilled, StarFilled } from '@ant-design/icons';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import type { ConversationMode } from '@/services/aiAgent.service';

dayjs.extend(relativeTime);

const { Text } = Typography;

export function conversationTypeLabel(mode: ConversationMode, intentHint?: string): string {
  if (intentHint) {
    const part = intentHint.split('_')[0];
    if (part === 'finance') return 'Finance Analysis';
    if (part === 'manufacturing') return 'Manufacturing Analysis';
    if (part === 'sales') return 'Sales Analysis';
    if (part === 'executive') return 'Executive Analysis';
    if (part === 'delivery') return 'Delivery Analysis';
    if (part === 'procurement') return 'Procurement Analysis';
  }
  if (mode === 'executive') return 'Executive Analysis';
  if (mode === 'analyst') return 'Analytical Report';
  return 'Assistant Chat';
}

export interface AIConversationCardProps {
  id: string;
  title: string;
  updatedAt: string;
  mode: ConversationMode;
  pinned?: boolean;
  favorite?: boolean;
  active?: boolean;
  typeHint?: string;
  onSelect: () => void;
  onRename?: () => void;
  onPin?: () => void;
  onFavorite?: () => void;
  onArchive?: () => void;
  onDuplicate?: () => void;
  onDelete?: () => void;
  onShare?: () => void;
}

export function AIConversationCard({
  title,
  updatedAt,
  mode,
  pinned,
  favorite,
  active,
  typeHint,
  onSelect,
  onRename,
  onPin,
  onFavorite,
  onArchive,
  onDuplicate,
  onDelete,
  onShare,
}: AIConversationCardProps) {
  const menuItems: MenuProps['items'] = [
    onRename ? { key: 'rename', label: 'Rename' } : null,
    onPin ? { key: 'pin', label: pinned ? 'Unpin' : 'Pin' } : null,
    onFavorite ? { key: 'fav', label: favorite ? 'Unfavorite' : 'Favorite' } : null,
    onDuplicate ? { key: 'dup', label: 'Duplicate' } : null,
    onShare ? { key: 'share', label: 'Share' } : null,
    onArchive ? { key: 'arch', label: 'Archive' } : null,
    onDelete ? { key: 'del', label: 'Delete', danger: true } : null,
  ].filter(Boolean) as MenuProps['items'];

  const onMenu: MenuProps['onClick'] = ({ key, domEvent }) => {
    domEvent.stopPropagation();
    if (key === 'rename') onRename?.();
    if (key === 'pin') onPin?.();
    if (key === 'fav') onFavorite?.();
    if (key === 'dup') onDuplicate?.();
    if (key === 'share') onShare?.();
    if (key === 'arch') onArchive?.();
    if (key === 'del') onDelete?.();
  };

  return (
    <div
      className={`ai-conversation-card${active ? ' active' : ''}`}
      onClick={onSelect}
      role="button"
      tabIndex={0}
      aria-label={`Conversation: ${title}`}
      onKeyDown={(e) => {
        if (e.key === 'Enter') onSelect();
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
        <Text strong ellipsis style={{ flex: 1, fontSize: 13 }}>
          {pinned ? <PushpinFilled style={{ color: '#1677ff', marginRight: 6 }} /> : null}
          {favorite ? <StarFilled style={{ color: '#faad14', marginRight: 6 }} /> : null}
          {title}
        </Text>
        <Dropdown menu={{ items: menuItems, onClick: onMenu }} trigger={['click']}>
          <MoreOutlined onClick={(e) => e.stopPropagation()} style={{ padding: 4 }} />
        </Dropdown>
      </div>
      <div style={{ marginTop: 6, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <Text type="secondary" style={{ fontSize: 11 }}>
          {dayjs(updatedAt).fromNow()}
        </Text>
        <Tag style={{ margin: 0, fontSize: 10, lineHeight: '18px' }}>{conversationTypeLabel(mode, typeHint)}</Tag>
      </div>
    </div>
  );
}
